// src/features/dashboard/components/StatsGrid.tsx
import { StatCard } from './StatCard';
import { StatCardSkeleton } from './ui/Skeleton';
import type { StatCardData, StatIconKey } from '../types/dashboard.types';

interface StatsGridProps {
  stats: StatCardData[];
  isInitialLoading: boolean;
}

/**
 * Sales, Active Customers, and Wallet Balance render in the Hero KPI row
 * (see DashboardPage.tsx / KPICard.tsx), and active services now has its
 * own dedicated widget (see ServiceSummary.tsx), so StatsGrid only shows
 * the remaining two -- Orders, Tickets -- to avoid duplicating the same
 * metrics twice on one page. This is a presentation-only filter: the
 * `stats` prop, its data contract, and useDashboardData are untouched.
 *
 * Dashboard V3 Phase 2: wrapped in a semantic `<section>` so this widget
 * carries its own "Business Snapshot" landmark identity within
 * DashboardPage's composition, instead of being an unlabeled grid
 * floating between sections. Column layout (auto-fit/minmax) and card
 * rendering are unchanged.
 */
const VISIBLE_ICONS: StatIconKey[] = ['orders', 'tickets'];

export function StatsGrid({ stats, isInitialLoading }: StatsGridProps) {
  const visibleStats = stats.filter((stat) => VISIBLE_ICONS.includes(stat.icon));

  return (
    <section aria-label="Business Snapshot stats" className="vania-stats-grid gap-4">
      {isInitialLoading
        ? Array.from({ length: 3 }).map((_, i) => <StatCardSkeleton key={i} />)
        : visibleStats.map((stat, index) => <StatCard key={stat.id} data={stat} index={index} />)}
    </section>
  );
}
